import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Plane, Hotel, Car, Map, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compact NRI-friendly enquiry form. No login required — we just capture
// enough context for a travel curator to reply within 24h with an
// EMI-ready plan tailored to the family.
export default function EnquiryModal({ open, onClose }) {
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    origin_country: "",
    origin_city: "",
    destination: "",
    travel_start: "",
    travel_end: "",
    duration_nights: "",
    adults: 2,
    children: 0,
    infants: 0,
    package_preference: "any",
    include_flight: true,
    include_hotel: true,
    include_transfer: true,
    include_itinerary: true,
    wants_emi: true,
    emi_months: 6,
    budget_inr: "",
    notes: "",
  });

  if (!open) return null;

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.destination.trim()) {
      toast.error("Please fill name, email, phone and destination");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        adults: Number(form.adults) || 1,
        children: Number(form.children) || 0,
        infants: Number(form.infants) || 0,
        duration_nights: form.duration_nights ? Number(form.duration_nights) : undefined,
        emi_months: form.wants_emi && form.emi_months ? Number(form.emi_months) : undefined,
        budget_inr: form.budget_inr ? Number(form.budget_inr) : undefined,
        origin_country: form.origin_country || undefined,
        origin_city: form.origin_city || undefined,
        travel_start: form.travel_start || undefined,
        travel_end: form.travel_end || undefined,
        notes: form.notes || undefined,
        package_preference: form.package_preference || "any",
      };
      await axios.post(`${API}/enquiries`, payload);
      toast.success("Enquiry sent! Our curator will email you within 24 hours.");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't send. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Couldn't send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const Toggle = ({ id, label, icon: Icon, on }) => (
    <button
      type="button"
      data-testid={`enquiry-toggle-${id}`}
      onClick={() => set(`include_${id}`, !on)}
      className={`flex items-center gap-2 px-3 py-2 rounded-full text-xs sm:text-sm border transition-all ${
        on
          ? "bg-[#D4AF37] text-black border-[#D4AF37]"
          : "bg-transparent text-white/70 border-white/20 hover:border-white/40"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center px-3 sm:px-6 py-6 overflow-y-auto"
      data-testid="enquiry-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl bg-[#0b0b10] border border-[#D4AF37]/25 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/60 hover:text-white"
          data-testid="enquiry-close-button"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="px-6 sm:px-8 pt-8 pb-4">
          <p className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]/80 mb-1">Talk to a curator</p>
          <h3 className="font-serif text-2xl sm:text-3xl text-white leading-tight">Plan your trip home, on EMI</h3>
          <p className="text-sm text-white/60 mt-2">
            Tell us where you'd love to travel and we'll email an EMI-ready plan within 24 hours. No signup needed.
          </p>
        </div>

        <div className="px-6 sm:px-8 pb-8 space-y-5">
          {/* Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70 text-xs">Full name*</Label>
              <Input
                data-testid="enquiry-name-input"
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Priya Sharma"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Email*</Label>
              <Input
                data-testid="enquiry-email-input"
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                placeholder="you@example.com"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Phone (with country code)*</Label>
              <Input
                data-testid="enquiry-phone-input"
                value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+1 415 555 1234"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Currently living in</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <Input
                  data-testid="enquiry-origin-city-input"
                  value={form.origin_city}
                  onChange={(e) => set("origin_city", e.target.value)}
                  placeholder="City"
                  className="bg-white/5 border-white/15 text-white"
                />
                <Input
                  data-testid="enquiry-origin-country-input"
                  value={form.origin_country}
                  onChange={(e) => set("origin_country", e.target.value)}
                  placeholder="Country"
                  className="bg-white/5 border-white/15 text-white"
                />
              </div>
            </div>
          </div>

          {/* Travel details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70 text-xs">Destination / home city*</Label>
              <Input
                data-testid="enquiry-destination-input"
                value={form.destination}
                onChange={(e) => set("destination", e.target.value)}
                placeholder="Mumbai, Kochi, Jaipur…"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Trip length (nights)</Label>
              <Input
                data-testid="enquiry-nights-input"
                type="number"
                min={0}
                max={365}
                value={form.duration_nights}
                onChange={(e) => set("duration_nights", e.target.value)}
                placeholder="e.g. 14"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Approx start</Label>
              <Input
                data-testid="enquiry-start-input"
                value={form.travel_start}
                onChange={(e) => set("travel_start", e.target.value)}
                placeholder="Dec 2026 or flexible"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
            </div>
            <div>
              <Label className="text-white/70 text-xs">Approx end</Label>
              <Input
                data-testid="enquiry-end-input"
                value={form.travel_end}
                onChange={(e) => set("travel_end", e.target.value)}
                placeholder="Jan 2027 or flexible"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
            </div>
          </div>

          {/* Travellers */}
          <div>
            <Label className="text-white/70 text-xs">Travellers</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[
                { k: "adults", label: "Adults", min: 1 },
                { k: "children", label: "Children (2-11)", min: 0 },
                { k: "infants", label: "Infants (<2)", min: 0 },
              ].map((t) => (
                <div key={t.k}>
                  <Input
                    data-testid={`enquiry-${t.k}-input`}
                    type="number"
                    min={t.min}
                    max={30}
                    value={form[t.k]}
                    onChange={(e) => set(t.k, e.target.value)}
                    className="bg-white/5 border-white/15 text-white"
                  />
                  <p className="text-[10px] text-white/50 mt-1">{t.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Package + Include toggles */}
          <div>
            <Label className="text-white/70 text-xs">Package preference</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {["any", "basic", "premium", "luxury"].map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`enquiry-pkg-${p}`}
                  onClick={() => set("package_preference", p)}
                  className={`px-3 py-1.5 rounded-full text-xs capitalize border transition-all ${
                    form.package_preference === p
                      ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                      : "bg-transparent text-white/70 border-white/20 hover:border-white/40"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-white/70 text-xs">Include in the plan</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              <Toggle id="flight" label="Flights" icon={Plane} on={form.include_flight} />
              <Toggle id="hotel" label="Hotels" icon={Hotel} on={form.include_hotel} />
              <Toggle id="transfer" label="Transfers" icon={Car} on={form.include_transfer} />
              <Toggle id="itinerary" label="Itinerary" icon={Map} on={form.include_itinerary} />
            </div>
            <p className="text-[11px] text-white/40 mt-2">Turn off anything you've already booked or don't need.</p>
          </div>

          {/* EMI + budget */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-white/10 bg-white/5 p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  data-testid="enquiry-emi-checkbox"
                  type="checkbox"
                  checked={form.wants_emi}
                  onChange={(e) => set("wants_emi", e.target.checked)}
                  className="accent-[#D4AF37]"
                />
                <span className="text-sm text-white">I want to pay on EMI</span>
              </label>
              {form.wants_emi && (
                <div className="mt-2">
                  <Label className="text-white/70 text-xs">EMI months</Label>
                  <Input
                    data-testid="enquiry-emi-months-input"
                    type="number"
                    min={1}
                    max={36}
                    value={form.emi_months}
                    onChange={(e) => set("emi_months", e.target.value)}
                    className="mt-1 bg-white/5 border-white/15 text-white"
                  />
                </div>
              )}
            </div>
            <div>
              <Label className="text-white/70 text-xs">Rough budget (₹, optional)</Label>
              <Input
                data-testid="enquiry-budget-input"
                type="number"
                min={0}
                value={form.budget_inr}
                onChange={(e) => set("budget_inr", e.target.value)}
                placeholder="e.g. 300000"
                className="mt-1 bg-white/5 border-white/15 text-white"
              />
              <p className="text-[11px] text-white/40 mt-1">Helps us shortlist the right tier.</p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-white/70 text-xs">Anything else we should know?</Label>
            <textarea
              data-testid="enquiry-notes-input"
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Diet, accessibility, temple visits, working days at home, kids' school break…"
              className="mt-1 w-full rounded-md bg-white/5 border border-white/15 text-white text-sm px-3 py-2 focus:outline-none focus:border-[#D4AF37]/50"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={onClose}
              className="text-white/70 hover:text-white"
              data-testid="enquiry-cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-[#D4AF37] text-black hover:bg-[#E5C468] font-medium px-6"
              data-testid="enquiry-submit-button"
            >
              {submitting ? (
                <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Sending…</span>
              ) : "Submit enquiry"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
